import { CommonModule, NgClass } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IPrintableReportHeading } from '@models/report-print-heading';
import { NgbModule, NgbPagination, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { LetDirective } from '@ngrx/component';
import { TranslateModule } from '@ngx-translate/core';
import { AudioPlayerComponent } from '@shared/audio-player/audio-player.component';
import { ButtonComponent } from '@shared/button/button.component';
import { ScoreBenchmark, scoreBenchmarkClassMap } from '@shared/enums/score-benchmark';
import { PreloaderModule } from '@shared/preloader/preloader.module';
import { PrintableReportHeadingComponent } from '@shared/printable-report-heading/printable-report-heading.component';
import { SortColumnsService, SortNgClassObject } from '@shared/sort-columns.service';
import { TooltipService } from '@shared/tooltip/tooltip.service';
import { ZbpDataTable } from '@shared/zbp-data-table/zbp-data-table.model';
import { ZbpIconComponent } from '@shared/zbp-icon/zbp-icon';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { NumberInputComponent } from '../number-input/number-input.component';
import CHECKBOX_COLUMN = ZbpDataTable.CHECKBOX_COLUMN;
import RADIO_COLUMN = ZbpDataTable.RADIO_COLUMN;
import PageChangeEvent = ZbpDataTable.PageChangeEvent;
import CommonColumnProperties = ZbpDataTable.CommonColumnProperties;

@Component({
  selector: 'zbp-data-table',
  imports: [
    PreloaderModule,
    LetDirective,
    ButtonComponent,
    NgbPagination,
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    NgbModule,
    ZbpIconComponent,
    NumberInputComponent,
    PrintableReportHeadingComponent,
    AudioPlayerComponent,
    TranslateModule
  ],
  templateUrl: './zbp-data-table.component.html'
})
export class ZbpDataTableComponent implements OnInit, OnChanges, AfterViewChecked {

  protected readonly EMPTY_FORM_GROUP = new FormGroup({});

  /// TABLE DATA CONTROLS

  /** Controls the column header data. */
  @Input({ required: true }) columns: ZbpDataTable.Column[] = [];

  /** Controls the row data. */
  @Input({ required: true }) rows: ZbpDataTable.Row[] = [];

  /** Controls the table fixed property. */
  @Input() tableFixed: boolean;

  /**
   * Prepended to the ids of the collapsible rows, so that tables nested within a collapsible row do
   * not reuse the ids of the table they sit inside of.
   *
   * Required for tables with collapsible nested tables.
   */
  @Input() uniqueIdentifierPrefix: string = '';

  /**
   * Adds the {@link PrintableReportHeadingComponent} to top of table header
   * that prints on each page when entries exist.
   */
  @Input() printableHeaders:  IPrintableReportHeading[] = [];

  /** Controls if colors will be used for print */
  @Input() colorPrint: boolean = false;

  /** Used by cell types with Form Controls when table rows are part of a form. */
  @Input() formArray: FormArray<FormGroup<Record<string, FormControl>>> = new FormArray([]);

  /** If true, a spinner will be displayed instead of rendering the table rows */
  @Input() isLoading: boolean = false;

  /** Will be shown when `isLoading === false` and `rows.length === 0` */
  @Input() noResultsMessage: string = 'No results found';

  /** Will be shown when not null, `isLoading === false`, and `rows.length === 0` */
  @Input() noResultsCustomTemplate: TemplateRef<any> = null;

  /** Emitted when the user clicks on a sortable column heading, changing the sort value */
  @Output() orderByParamChange = new EventEmitter<string>();

  /** Style theme applied to table */
  @Input() theme: ZbpDataTable.TableTheme = ZbpDataTable.TableTheme.blueAndWhite;

  /** Flag to allow the table to be scrollable */
  @Input() isScrollable: boolean = false;

  @Input() leftFooterContent: TemplateRef<any>;
  @Input() rightFooterContent: TemplateRef<any>;

  /// PAGINATION CONTROLS

  /** See {@link NgbPagination#pageSize}. */
  @Input() pageSize: number = 25;

  /** See {@link NgbPagination#maxSize}. */
  @Input() maxPageCount: number = 10;

  /** See {@link NgbPagination#collectionSize}. */
  @Input() collectionSize: number;

  /** See {@link NgbPagination#page}. */
  @Input() page: number;

  /** See {@link NgbPagination#pageChange}. */
  @Output() pageChange = new EventEmitter<PageChangeEvent>();

  /// PRIVATE FIELDS

  private _sortColumnKey: string | null = null;
  private _sortDirection: boolean | null = null;

  columnLength = 1;

  /** Used to track original states of the collapsible rows by
   * {@link ZbpDataTableComponent.expandAllCollapsibleRows}
   * and {@link ZbpDataTableComponent.restoreCollapsibleRowStates}.
  */
  private originalAccordionStates = new Map<Element, boolean>();

  /// INTERNAL PUBLIC FIELDS

  @ViewChild('pagination') pagination: NgbPagination;
  @ViewChild('tableScrollWrapper') tableScrollWrapper: ElementRef;
  public _checkAllStateForm = new FormControl<boolean>(false);

  /// READONLY FIELDS

  protected readonly ZbpDataTable = ZbpDataTable;
  readonly zbpIconNames = zbpIconNames;

  scrollActive = false;

  constructor(
    private sortService: SortColumnsService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private tooltipService: TooltipService,
  ) {
  }

  onMouseLeave(tooltip: NgbTooltip): void {
    this.tooltipService.handleTooltipHidden(tooltip);
  }

  onMouseEnter(tooltip: NgbTooltip): void {
    this.tooltipService.handleTooltipVisible(tooltip, true);
  }

  pageChangeWrapper(page: number) {
    this.pageChange.emit({ page, pagination: this.pagination });
  }

  ngAfterViewChecked(): void {
    this.isScrollActive();
  }

  ngOnInit() {
    this.calculateCheckAllState();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.isLoading && changes.isLoading.currentValue === false) {
      // no longer loading, calculate check all state
      this.calculateCheckAllState();
    }

    if (changes.columns) {
      this.columnLength = changes.columns.currentValue
        .map((column: CommonColumnProperties): number => column.columnSpan ?? 1)
        .reduce((accumulator: number, currentValue: number) => accumulator + currentValue, 0);
    }
  }

  hasCollapsibleCell(row: ZbpDataTable.Row): boolean {
    return Object.values(row.cells).some(item => item.type === ZbpDataTable.COLLAPSIBLE_CELL);
  }

  getCheckboxColumn(): ZbpDataTable.Column {
    return this.columns.find(column => column.type === CHECKBOX_COLUMN);
  }

  isScrollActive() {
    if (!this.isScrollable || !this.tableScrollWrapper) {
      return;
    }

    const el = this.tableScrollWrapper.nativeElement;
    this.scrollActive = el.scrollHeight > el.clientHeight;
    this.cdr.detectChanges();
  }

  getRadioControl(column: ZbpDataTable.Column): FormControl {
    if (column.type !== RADIO_COLUMN) {
      return null; // only calculate state for radio column
    }
    return column.control;
  }

  checkboxCellClickHandler(checkboxCell: ZbpDataTable.CheckboxCell) {
    // Then call the additional click handler if provided
    if (checkboxCell.additionalClickHandler) {
      checkboxCell.additionalClickHandler();
    }
  }

  calculateCheckAllState(): void {
    const checkAllColumn = this.getCheckboxColumn();
    if (checkAllColumn) {
      this.calculateCheckAllStateForColumn(checkAllColumn);
    }
  }

  checkboxCellChangeHandler(column: ZbpDataTable.Column, checkboxCell: ZbpDataTable.CheckboxCell) {
    this.calculateCheckAllStateForColumn(column);

    // Then call the additional click handler if provided
    if (checkboxCell.additionalChangeHandler) {
      checkboxCell.additionalChangeHandler();
    }
  }

  calculateCheckAllStateForColumn(column: ZbpDataTable.Column) {
    if (column.type !== CHECKBOX_COLUMN) {
      return; // only calculate state for checkbox column
    }
    if (this.isLoading) {
      // assume nothing is checked when loading
      this._checkAllStateForm.setValue(false);
      return;
    }
    // we have a select all checkbox header, so we check to see if all the rows are selected already
    this._checkAllStateForm.setValue(column.formArray.controls
      .map(control => (control as FormGroup))
      .map(group => group?.get(column.key) as FormControl<boolean>)
      .every(control => control.value)
    );
  }

  toggleAllCheckboxes(column: ZbpDataTable.CheckboxColumn): void {
    this._checkAllStateForm.setValue(!this._checkAllStateForm.value);
    column.formArray.controls
      .map(control => (control as FormGroup))
      .map(group => group?.get(column.key) as FormControl<boolean>)
      .forEach(control => control?.setValue(this._checkAllStateForm.value));
  }

  /**
   * Uses the column's key to get a specific cell from the row.
   */
  getCell(row: ZbpDataTable.Row, column: ZbpDataTable.Column): ZbpDataTable.Cell {
    return row.cells[column.key] ?? {} as any;
  }

  /** The key for the column that is currently being sorted, or null if no column is selected. */
  get sortColumnKey(): string | null {
    return this._sortColumnKey;
  }

  /**
   * The sort direction for the selected column:
   * | Value | Direction    |
   * | ----- | ------------ |
   * | null  | no direction |
   * | false | descending   |
   * | true  | ascending    |
   */
  get sortDirection(): boolean | null {
    return this._sortDirection;
  }

  onSortColumnClick(columnKey: string): void {
    if (columnKey !== this._sortColumnKey) {
      // we clicked on a new column, switch to track the new column
      // and then reset the direction to null before running the rest of the logic
      this._sortColumnKey = columnKey;
      this._sortDirection = null;
    }
    // calculate the new sort direction based on the direction we have stored
    this._sortDirection = this.sortService.setSortState(this._sortDirection);
    // update the state of the service
    this.sortService.setSortParams(this._sortColumnKey, this._sortDirection);
    // notify subscribers
    this.orderByParamChange.emit(this.sortService.orderByParam);
  }

  getSortClass(column: string): SortNgClassObject {
    return this.sortService.setSortClass(
      column === this._sortColumnKey
        ? this._sortDirection
        : null
    );
  }

  getSortAriaLabel(column: ZbpDataTable.SortableColumn): string {
    const coreLabel = column.ariaLabel ?? column.header;
    const nextDirection: boolean | null = (column.key === this._sortColumnKey)
      ? this.sortService.setSortState(this._sortDirection)
      : true; // when the column is not selected, ascending is always the nex direction

    switch (nextDirection) {
    case true:
      return `currently not sorting ${coreLabel}, click to sort ascending`;
    case false:
      return `currently sorting ${coreLabel} ascending, click to sort descending`;
    default:
      return `currently sorting ${coreLabel} descending, click to remove sorting`;
    }
  }

  getSortAriaState(column: ZbpDataTable.SortableColumn): string {
    const nextDirection: boolean | null = (column.key === this._sortColumnKey)
      ? this.sortService.setSortState(this._sortDirection)
      : true; // when the column is not selected, ascending is always the next direction

    switch (nextDirection) {
    case true:
      return 'none';
    case false:
      return 'ascending';
    default:
      return 'descending';
    }
  }

  getBenchmarkLabelTranslationKey(benchmark: ScoreBenchmark): string {
    switch (benchmark) {
    case ScoreBenchmark.DoesNotMeet:
      return 'shared-benchmark-label-below';
    case ScoreBenchmark.Meets:
      return 'shared-benchmark-label-on-level';
    case ScoreBenchmark.Exceeds:
      return 'shared-benchmark-label-above-level';
    case ScoreBenchmark.NotScored:
    default:
      return 'shared-benchmark-label-n-a';
    }
  }

  getBenchmarkClass(benchmark: ScoreBenchmark): string {
    return scoreBenchmarkClassMap[benchmark];
  }

  toggleCollapsible(id: string): void {
    const toggleElement = document.getElementById(id);
    if (toggleElement?.classList?.contains('show')) {
      toggleElement?.classList?.remove('show');
    } else {
      toggleElement?.classList?.add('show');
    }
  }

  getCollapsibleRow(id: string) {
    return document.getElementById(id);
  }

  getAllCollapsibleRows(): NodeListOf<Element> {
    return this.elementRef.nativeElement.querySelectorAll('.collapsible-row');
  }

  /**
   * Opens all collapsible rows in the table unless expandSecondaryRows is false. In that case, we'll only expand
   * the top level rows, and we'll skip or collapse other rows.
   *
   * @param expandSecondaryRows - defaults to true. allows caller to specify if only the top level
   * rows should be expanded.
   * top level rows have a pattern of collapsible_row_X
   * secondary rows have a pattern of collapsible_row_X_Y_collapsible_row_Z
   *
   * Can call afterward {@link ZbpDataTableComponent.restoreCollapsibleRowStates}
   * to reset the rows back to their original (un)collapsed states.
   */
  expandAllCollapsibleRows(expandSecondaryRows: boolean = true): Map<Element, boolean> {
    // Clear out the old state
    this.originalAccordionStates.clear();

    const collapsibleRows = this.getAllCollapsibleRows();

    collapsibleRows.forEach((row) => {
      const wasExpanded = row.classList.contains('show');
      this.originalAccordionStates.set(row, wasExpanded);
      const isTopLevelRow = /^collapsible_row_\d+$/.test(row.id);

      if (!wasExpanded && (expandSecondaryRows || isTopLevelRow)) {
        row.classList.add('show');
      } else if (wasExpanded && !expandSecondaryRows && !isTopLevelRow) {
        row.classList.remove('show');
      }
    });

    return this.originalAccordionStates;
  }

  /**
   * Closes all collapsible rows in the table.
   *
   * Can call afterward {@link ZbpDataTableComponent.restoreCollapsibleRowStates}
   * to reset the rows back to their original (un)collapsed states.
   */
  collapseAllCollapsibleRows(): Map<Element, boolean> {
    // Clear out the old state
    this.originalAccordionStates.clear();

    const collapsibleRows = this.getAllCollapsibleRows();

    collapsibleRows.forEach((row) => {
      const wasExpanded = row.classList.contains('show');
      this.originalAccordionStates.set(row, wasExpanded);

      if (wasExpanded) {
        row.classList.remove('show');
      }
    });

    return this.originalAccordionStates;
  }

  /**
   * Can be called after {@link ZbpDataTableComponent.expandAllCollapsibleRows}
   * to restore the collapsible rows back to their original state
   */
  restoreCollapsibleRowStates(): void {
    this.originalAccordionStates.forEach((wasExpanded, row) => {
      if (!wasExpanded) {
        row.classList.remove('show');
      } else if (wasExpanded && !row.classList.contains('show')) {
        row.classList.add('show');
      }
    });
  }

  protected readonly Boolean = Boolean;
  protected readonly FormGroup = FormGroup;
}
